import type { PlanKey } from "@sagot/constants";
import { usePermissions } from "../../../../session/permissions";
import { serviceArea } from "../../../service/areas";
import { useServiceAreaAccess } from "../../../service/use-service-area";

/**
 * Whether the Service Desk can be used in this workspace, as the server's navigation says (`GET /v1/nav`):
 * `available` (the flag is on and the plan has it), `locked` (the plan lacks it: the builder says which plan),
 * `off` (the flag is off for the workspace: the area does not exist), or `unknown` (still loading, unreadable, or the
 * member cannot see the Service Desk at all, so nothing is guessed and nothing is locked).
 */
export type ServiceDeskState = "available" | "locked" | "off" | "unknown";

export interface ServiceDesk {
  state: ServiceDeskState;
  /** The lowest plan with the Service Desk. */
  minPlan: PlanKey;
}

export function useServiceDesk(): ServiceDesk {
  const permissions = usePermissions();
  const access = useServiceAreaAccess("tickets");
  const minPlan = serviceArea("tickets").minPlan;
  if (permissions.isPending || !permissions.can("tickets.view") || access.isPending || access.isError) return { state: "unknown", minPlan };
  if (access.locked) return { state: "locked", minPlan };
  return { state: access.enabled ? "available" : "off", minPlan };
}

/** Whether the Service Desk steps and triggers cannot be used right now (plan or flag): they are listed, marked, and not offered. */
export const serviceDeskBlocked = (state: ServiceDeskState): boolean => state === "locked" || state === "off";
